import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, finalize, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthApiService } from '../api/auth-api.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { UserDto } from '../api/_dtos/user.dto';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { LoginDialogComponent } from '../../features/auth/login-dialog/login-dialog';
import { TranslateService } from '@ngx-translate/core';
import { RegisterDto } from '../api/_dtos/register.dto';
import { CartService } from '../../shared/services/cart.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public redirectUrl: string | null = null;
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';
  private readonly ADMIN_CUSTOMERS_PATH = 'admin/dashboard';
  private readonly HOME_PATH = 'home';
  private userSubject = new BehaviorSubject<UserDto | null>(null);
  public currentUser$ = this.userSubject.asObservable();

  constructor(
    private localStorageService: LocalStorageService,
    private api: AuthApiService,
    private router: Router,
    private injector: Injector
  ) {
    this.initializeAuthentication();
  }

  // Lazy inject CartService to avoid circular dependency
  private get cartService(): CartService {
    return this.injector.get(CartService);
  }

  login(credentials: { email: string; password: string }) {
    return this.api.login(credentials).pipe(
      tap(response => {
        if (response) {
          this.setData(response);
          this.cartService.reloadAfterLogin();
        }
      })
    );
  }

  register(data: RegisterDto) {
    return this.api.register(data).pipe(
      tap(response => {
        if (response) {
          this.setData(response);
          this.cartService.reloadAfterLogin();
        }
      })
    );
  }

  forgotPassword(email: string) {
    return this.api.forgotPassword(email);
  }

  /**
   * Get OAuth redirect URL for a provider
   */
  getOAuthUrl(provider: 'google' | 'apple') {
    return this.api.getOAuthUrl(provider);
  }

  /**
   * Authenticate with OAuth provider
   */
  socialLogin(provider: 'google' | 'apple', token: string) {
    return this.api.socialLogin(provider, token).pipe(
      tap(response => {
        if (response) {
          this.setData(response);
          this.cartService.reloadAfterLogin();
        }
      })
    );
  }

  /**
   * Link OAuth provider to existing account
   */
  linkProvider(provider: 'google' | 'apple', token: string) {
    return this.api.linkProvider(provider, token);
  }

  /**
   * Complete OAuth login using backend-issued auth token from callback popup.
   * Merges guest cart via regular cart API call, then clears guest cart id.
   */
  completeOAuthLogin(token: string): Observable<void> {
    this.localStorageService.setItem(this.TOKEN_KEY, token);

    return this.api.getMe().pipe(
      map(res => res.user),
      tap(user => this.setData({ token, user })),
      switchMap(() => {
        const guestCartId = this.cartService.getGuestCartId();
        if (!guestCartId) {
          return of(void 0);
        }

        return this.cartService.refresh().pipe(
          tap(() => this.cartService.clearGuestCartId()),
          map(() => void 0)
        );
      }),
      catchError(error => {
        this.clearUserData();
        return throwError(() => error);
      })
    );
  }

  getToken(): string | null {
    return this.localStorageService.getItem<string>(this.TOKEN_KEY);
  }

  getUser(): UserDto | null {
    return this.localStorageService.getItem<UserDto>(this.USER_KEY);
  }

  getRoles(): string[] {
    return this.userSubject.value?.roles ?? [];
  }

  checkIsLoggedIn(): boolean {
    return !!this.userSubject.value;
  }

  public clearUserData(): void {
    this.localStorageService.removeItem(this.TOKEN_KEY);
    this.localStorageService.removeItem(this.USER_KEY);
    this.userSubject.next(null);
  }

  logout() {
    if (!this.checkIsLoggedIn()) {
      this.clearUserData();
      this.router.navigate(['/']);
      return;
    }

    this.api.logout().pipe(
      catchError(() => of(null)),
      finalize(() => {
        this.clearUserData();
        this.cartService.reloadAfterLogin();
        this.router.navigate(['/']);
      })
    ).subscribe();
  }

  handleLoginDialog(redirectUrl?: string, message?: string, options?: { fallbackUrl?: string; origin?: 'forced' | 'voluntary' }): Observable<boolean> {
    const dialogService = this.injector.get(DialogService);
    const fallbackUrl = options?.fallbackUrl ?? '/';
    const origin = options?.origin ?? (redirectUrl ? 'forced' : 'voluntary');

    if (redirectUrl) {
      this.redirectUrl = redirectUrl;
    }

    const ref = dialogService.open(LoginDialogComponent, {
      closable: false,
      modal: true,
      breakpoints: { '640px': '95vw' },
      style: { width: '450px' },
      contentStyle: { overflow: 'visible' },
      data: {
        message,
        redirectUrl
      },
    });

    return ref!.onClose.pipe(
      map((res: any) => {
        if (res?.success) {
          if (res.redirectUrl) {
            this.redirectUrl = res.redirectUrl;
          }
          this.doRedirectAfterLogin();
          return true;
        }
        // Only redirect to fallback if modal was forced (guard/interceptor)
        if (origin === 'forced') {
          this.router.navigateByUrl(fallbackUrl);
        }
        return false;
      })
    );
  }

  private initializeAuthentication(): void {
    const token = this.getToken();

    if (!token) {
      this.clearUserData();
      return;
    }

    const cachedUser = this.getUser();
    if (cachedUser) {
      this.userSubject.next(cachedUser);
    }

    Promise.resolve().then(() => {
      this.api.getMe().pipe(
        map(res => res.user),
        tap((freshUser: UserDto) => {
          this.localStorageService.setItem(this.USER_KEY, freshUser);
          this.userSubject.next(freshUser);
        }),
        catchError(() => {
          this.clearUserData();
          return of(null);
        })
      ).subscribe();
    });
  }

  private setData(data: { token: string; user: UserDto }): void {
    this.localStorageService.setItem(this.TOKEN_KEY, data.token);
    this.localStorageService.setItem(this.USER_KEY, data.user);
    this.userSubject.next(data.user);
  }

  private doRedirectAfterLogin() {
    const lang = this.injector.get(TranslateService).getCurrentLang();

    if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl);
      this.redirectUrl = null;
      return;
    }

    const roles = this.getRoles();
    if (roles.includes('admin')) {
      this.router.navigate([`/${lang}/${this.ADMIN_CUSTOMERS_PATH}`]);
    } else {
      this.router.navigate([`/${lang}/${this.HOME_PATH}`]);
    }
  }
}
